import React, { useState, useEffect } from 'react';
import { sedesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Building, Power, MapPin, Users, ChevronDown, ChevronUp, Coffee } from 'lucide-react';

const Sedes = () => {
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [sedeEditando, setSedeEditando] = useState(null);
  const [usuariosSede, setUsuariosSede] = useState({});
  const [expandedSede, setExpandedSede] = useState(null);

  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    endereco: '',
    almoco_inicio: '',
    almoco_fim: '',
  });

  useEffect(() => {
    carregarSedes();
  }, []);

  const carregarSedes = async () => {
    setLoading(true);
    try {
      const response = await sedesAPI.listar();
      setSedes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar sedes');
    } finally {
      setLoading(false);
    }
  };

  const carregarUsuariosSede = async (sedeId) => {
    if (expandedSede === sedeId) {
      setExpandedSede(null);
      return;
    }
    try {
      const response = await sedesAPI.listarUsuarios(sedeId);
      setUsuariosSede(prev => ({ ...prev, [sedeId]: response.data }));
      setExpandedSede(sedeId);
    } catch (error) {
      toast.error('Erro ao carregar usuários da sede');
    }
  };

  const abrirModalNovo = () => {
    setSedeEditando(null);
    setFormData({ nome: '', codigo: '', endereco: '', almoco_inicio: '', almoco_fim: '' });
    setModalAberto(true);
  };

  const abrirModalEditar = (sede) => {
    setSedeEditando(sede);
    setFormData({
      nome: sede.nome,
      codigo: sede.codigo,
      endereco: sede.endereco || '',
      almoco_inicio: sede.almoco_inicio || '',
      almoco_fim: sede.almoco_fim || '',
    });
    setModalAberto(true);
  };

  const handleSalvar = async () => {
    if (!formData.nome.trim() || !formData.codigo.trim()) {
      toast.error('Nome e Código são obrigatórios');
      return;
    }

    setSalvando(true);
    try {
      if (sedeEditando) {
        await sedesAPI.atualizar(sedeEditando.id, formData);
        toast.success('Sede atualizada!');
      } else {
        await sedesAPI.criar(formData);
        toast.success('Sede criada!');
      }
      setModalAberto(false);
      carregarSedes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleInativar = async (sede) => {
    const acao = sede.ativo ? 'inativar' : 'ativar';
    if (!window.confirm(`Deseja ${acao} a sede "${sede.nome}"?`)) return;

    try {
      await sedesAPI.inativar(sede.id);
      toast.success(`Sede ${acao === 'inativar' ? 'inativada' : 'ativada'}!`);
      carregarSedes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status');
    }
  };

  const handleExcluir = async (sede) => {
    if (!window.confirm(`Excluir sede "${sede.nome}"? Só é possível se não houver usuários vinculados.`)) return;

    try {
      await sedesAPI.excluir(sede.id);
      toast.success('Sede excluída!');
      carregarSedes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6" data-testid="sedes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building className="h-7 w-7 text-red-600" />
            Sedes / Filiais
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Gerencie as sedes da empresa
          </p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={abrirModalNovo} data-testid="nova-sede-btn">
          <Plus className="mr-2 h-4 w-4" />
          Nova Sede
        </Button>
      </div>

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Regras do Sistema:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li><strong>Admin</strong>: CRUD completo, visualiza todas as sedes</li>
                <li><strong>Gerente</strong>: Visualiza todas as sedes e dados</li>
                <li><strong>Supervisor / Vendedor</strong>: Veem apenas dados da sua sede</li>
                <li><strong>BKO</strong>: Acesso a todas as sedes (recomendado)</li>
                <li>Sedes com usuários vinculados devem ser <strong>inativadas</strong>, não excluídas</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sedes Cadastradas ({sedes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
            </div>
          ) : sedes.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Building className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhuma sede cadastrada</p>
              <Button variant="outline" className="mt-4" onClick={abrirModalNovo}>
                <Plus className="mr-2 h-4 w-4" />
                Criar primeira sede
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {sedes.map((sede) => (
                <div key={sede.id} className={`border rounded-lg ${sede.ativo ? 'border-gray-200' : 'border-red-200 bg-red-50/30'}`}>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sede.ativo ? 'bg-red-100' : 'bg-gray-100'}`}>
                          <Building className={`h-5 w-5 ${sede.ativo ? 'text-red-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg" data-testid={`sede-nome-${sede.id}`}>{sede.nome}</h3>
                            <Badge variant="outline" className="text-xs">{sede.codigo}</Badge>
                            <Badge className={sede.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {sede.ativo ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </div>
                          {sede.endereco && (
                            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" /> {sede.endereco}
                            </p>
                          )}
                          {sede.almoco_inicio && sede.almoco_fim && (
                            <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                              <Coffee className="h-3 w-3" /> Almoco: {sede.almoco_inicio} - {sede.almoco_fim}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => carregarUsuariosSede(sede.id)} data-testid={`ver-usuarios-sede-${sede.id}`}>
                          <Users className="h-4 w-4 mr-1" />
                          {expandedSede === sede.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => abrirModalEditar(sede)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleInativar(sede)} className={sede.ativo ? 'text-amber-600' : 'text-green-600'}>
                          <Power className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExcluir(sede)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {expandedSede === sede.id && (
                    <div className="border-t px-4 py-3 bg-gray-50">
                      <p className="text-sm font-medium text-gray-700 mb-2">Usuários vinculados:</p>
                      {(usuariosSede[sede.id] || []).length === 0 ? (
                        <p className="text-sm text-gray-500">Nenhum usuário vinculado</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {(usuariosSede[sede.id] || []).map(u => (
                            <Badge key={u.id} variant="outline" className="text-xs">
                              {u.nome} ({u.perfil})
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {sedeEditando ? 'Editar Sede' : 'Nova Sede'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Nome da Sede <span className="text-red-500">*</span></Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Sede Centro SP"
                className="mt-1"
                data-testid="sede-nome-input"
              />
            </div>
            <div>
              <Label>Codigo <span className="text-red-500">*</span></Label>
              <Input
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                placeholder="Ex: SP-001"
                className="mt-1"
                data-testid="sede-codigo-input"
              />
            </div>
            <div>
              <Label>Endereco</Label>
              <Input
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                placeholder="Endereco completo da sede..."
                className="mt-1"
                data-testid="sede-endereco-input"
              />
            </div>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <Label className="text-amber-800 font-semibold flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                Horario de Almoco
              </Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs text-amber-600">Inicio</Label>
                  <Input
                    type="time"
                    value={formData.almoco_inicio}
                    onChange={(e) => setFormData({ ...formData, almoco_inicio: e.target.value })}
                    className="mt-1"
                    data-testid="sede-almoco-inicio"
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-600">Fim</Label>
                  <Input
                    type="time"
                    value={formData.almoco_fim}
                    onChange={(e) => setFormData({ ...formData, almoco_fim: e.target.value })}
                    className="mt-1"
                    data-testid="sede-almoco-fim"
                  />
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Define o horario de bloqueio automatico de almoco para todos da sede (exceto Admin).
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvar} disabled={salvando} className="bg-red-600 hover:bg-red-700" data-testid="salvar-sede-btn">
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {sedeEditando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sedes;
